import { inject, Injectable, OnDestroy } from "@angular/core";
import { LoggerService } from "../../ancestors/logger.service";
import { ClientAuditApiService, ClientErrorPayload, PageViewPayload } from "./api/client-audit-api.service";
import { TokenManagerService } from "./token-manager.service";

/**
 * Lunghezza massima del messaggio di errore per evitare payload troppo grandi
 */
const MAX_ERROR_MESSAGE_LENGTH = 2000;

/**
 * Servizio di audit client.
 * Cattura errori e navigazioni di pagina e li invia immediatamente al backend uno alla volta.
 *
 * Il servizio genera un traceId di sessione (UUID) per correlare tutti gli eventi
 * di una singola sessione utente.
 */
@Injectable({ providedIn: "root" })
export class ClientAuditService implements OnDestroy {
  private logger = inject(LoggerService);

  private clientAuditApi = inject(ClientAuditApiService);

  private tokenManager = inject(TokenManagerService);

  /** Flag per indicare se il servizio è stato inizializzato */
  private initialized = false;

  /** Flag per prevenire chiamate ricorsive a trackError (evita loop in caso di errore nella chiamata audit) */
  private isTrackingError = false;

  /** TraceId di sessione (generato una volta per sessione browser) */
  private readonly SESSION_TRACE_ID: string;

  constructor() {
    this.logger.setCaller("ClientAuditService");
    this.SESSION_TRACE_ID = crypto.randomUUID();
  }

  /**
   * Inizializza il servizio di audit client.
   * Deve essere chiamato una volta sola, tipicamente nell'AppComponent.
   */
  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.logger.info(`Client audit service initialized (traceId: ${this.SESSION_TRACE_ID})`);
  }

  /**
   * Traccia un errore client e lo invia immediatamente al backend.
   * Viene chiamato dal GlobalError handler.
   * @param errorMessage Messaggio di errore da tracciare
   */
  trackError(errorMessage: string): void {
    if (!this.initialized || this.isTrackingError || !this.tokenManager.isAuthenticated()) {
      return;
    }

    const payload: ClientErrorPayload = {
      errorMessage: this.sanitizeErrorMessage(errorMessage),
      traceId: this.SESSION_TRACE_ID,
      userAgent: navigator.userAgent,
      clientTimestamp: new Date()
        .toISOString(),
      pageUrl: window.location.href
    };

    // Flag per prevenire chiamate ricorsive: se sendClientError fallisce
    // e l'errore viene catturato da GlobalError, non richiamiamo trackError
    this.isTrackingError = true;

    // Invio immediato - fire and forget, non blocca il thread principale
    this.clientAuditApi.sendClientError(payload)
      .catch(() => {
        this.logger.warn("Failed to send client error audit");
      })
      .finally(() => {
        this.isTrackingError = false;
      });
  }

  /**
   * Traccia una navigazione di pagina e la invia immediatamente al backend.
   * Viene chiamato dall'AppComponent su NavigationEnd.
   * @param route Route della pagina visitata
   * @param queryParams Parametri query della route (opzionale)
   */
  trackPageView(route: string, queryParams?: Record<string, unknown>): void {
    if (!this.initialized || !this.tokenManager.isAuthenticated()) {
      return;
    }

    const payload: PageViewPayload = {
      route,
      queryParams: queryParams || undefined,
      traceId: this.SESSION_TRACE_ID,
      userAgent: navigator.userAgent,
      clientTimestamp: new Date()
        .toISOString(),
      referrer: document.referrer || undefined
    };

    // Invio immediato - fire and forget, non blocca il thread principale
    this.clientAuditApi.sendPageView(payload)
      .catch(() => {
        this.logger.warn("Failed to send page view audit");
      });
  }

  /**
   * Restituisce il traceId di sessione corrente
   */
  getSessionTraceId(): string {
    return this.SESSION_TRACE_ID;
  }

  ngOnDestroy(): void {
    // Nessun cleanup necessario - gli eventi sono inviati immediatamente
  }

  /**
   * Sanitizza il messaggio di errore:
   * - Tronca a MAX_ERROR_MESSAGE_LENGTH caratteri
   * - Rimuove eventuali dati sensibili (token, password)
   */
  private sanitizeErrorMessage(message: string): string {
    if (!message) {
      return "Unknown error";
    }

    let sanitized = message;

    // Tronca i messaggi troppo lunghi
    if (sanitized.length > MAX_ERROR_MESSAGE_LENGTH) {
      sanitized = sanitized.substring(0, MAX_ERROR_MESSAGE_LENGTH) + "... [truncated]";
    }

    // Rimuovi eventuali token JWT (pattern: eyJ...xxx.eyJ...xxx.xxx)
    sanitized = sanitized.replace(/eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, "[JWT_REDACTED]");

    // Rimuovi pattern di password in chiaro (es: password=xxx, "password":"xxx")
    sanitized = sanitized.replace(/(?:password|passwd|pwd|secret|token)[=:]["']?[^\s"',}]+/gi, "[REDACTED]");

    return sanitized;
  }
}
